import React, { useEffect, useCallback, useState } from "react";
import { Link } from "react-router-dom";
import useAuthStore from "@/store/useAuthStore";
import { logoutApi } from "@/api";
import {
  Home,
  Map,
  Calendar,
  CalendarCheck,
  LogOut,
  Shield,
  UserRound,
  LocateFixed,
  MessageSquare,
  MessagesSquare,
  Bell,
  Wrench,
  Activity,
  Crown,
  Building2,
  Store,
  Bike,
  Users,
  UserCheck,
  BarChart3,
  CreditCard,
  HelpCircle,
  TrendingUp,
  Columns,
  Eye,
  DollarSign,
  FileText,
  Megaphone,
  ChevronRight,
  Swords,
  Trophy,
  Tag,
} from "lucide-react";
import {
  getBoardUnreadCount,
  getDmUnreadCount,
  getEventNotificationsUnreadCount,
  getInAppUnreadCount,
} from "@/api";
import { fetchUnreadCount as fetchConnectionsUnread } from "@/services/connectionsApi";
// FEAT DM-BADGE (Set 2026): badge non-letto solo per messaggi altrui
import { shouldIncrementDmUnread } from "@/utils/dmBadge";
import { useDealerNotifications } from "@/hooks/domain/useDealerNotifications";
import useNotifStore from "@/store/useNotifStore";
import {
  useWebSocket,
  WsIncomingMessage,
} from "@/hooks/infrastructure/useWebSocket";
import { useTheme } from "@/hooks/useTheme";
import RiderPillNav from "@/components/nav/RiderPillNav";
import AdminMobileNav from "@/components/nav/AdminMobileNav";
import useIsActive from "@/components/nav/useIsActive";
import {
  SidebarLink,
  SidebarLinkBadge,
  SectionHeader,
} from "@/components/nav/SidebarItems";
import DealerPillNav from "@/components/nav/DealerPillNav";

/* ─────────────────────────────────────────────────────────────────
   Shared helpers
───────────────────────────────────────────────────────────────── */

// Cache condivisa dei contatori non letti (60s TTL) — evita di ripetere
// 3 chiamate unread-count a OGNI navigazione e di superare i rate limit.
const unreadCache: {
  ts: number;
  board: number;
  event: number;
  dm: number;
  inapp: number;
} = {
  ts: 0,
  board: 0,
  event: 0,
  dm: 0,
  inapp: 0,
};

/* ─────────────────────────────────────────────────────────────────
   Dealer mobile pill nav — Priority+ pattern (4 tab primari + overflow)
   QW10: ridotto da 9 voci a 4 + bottom sheet overflow.
   Pattern ispirato a Spotify/YouTube Studio: max 5 tab, l'overflow
   apre un menu in basso con le voci secondarie.
───────────────────────────────────────────────────────────────── */

/* ─────────────────────────────────────────────────────────────────
   Admin mobile nav — Priority+ pattern (4 tab + overflow)
   Risolve il problema: admin su smartphone aveva solo 2 link.
   Ora: Dashboard, Utenti, Moderazione, Altro (bottom sheet con le altre voci).
───────────────────────────────────────────────────────────────── */

/* ─────────────────────────────────────────────────────────────────
   Main Navbar component
───────────────────────────────────────────────────────────────── */

const Navbar: React.FC<{ collapsed?: boolean; onToggle?: () => void }> = ({
  collapsed = false,
  onToggle,
}) => {
  const { user, logout } = useAuthStore();
  const {
    boardUnread,
    eventUnread,
    dmUnread,
    inAppUnread,
    setAll,
    incrementDmUnread,
  } = useNotifStore();
  const { affiliationRequests, chapterRequests } = useDealerNotifications();

  // FIX CERCHIA (Ago 2026): contatore richieste di connessione biker in sospeso
  // (mostrato come badge sul link Cerchia della sidebar).
  const [connectionPending, setConnectionPending] = useState(0);

  // FIX HOOKS (19 ott 2026, ESLint react-hooks/rules-of-hooks): `useIsActive()`
  // era chiamato dentro il JSX in un blocco condizionale (riga ~1688) e questo
  // cambia il numero di hook fra un render e l'altro → React #310. Ora è qui.
  const isActivePath = useIsActive();

  const isAdmin = user?.user_type === "admin";
  const localePending = useAuthStore((s) => s.localePending);
  // LOCALE/TAPPA (Ott 2026): il locale accede come "dealer" ma non ha
  // abbonamento né chapter → niente sidebar concessionario.
  //
  // Il flag `is_locale` può non essere ancora noto: all'avvio l'app usa lo
  // utente salvato e verifica in background (LocaleResolver). In quella
  // finestra NON mostriamo il menu concessionario (era il "menu dealer che
  // appare e sparisce"), ma l'attesa è limitata: oltre il timeout l'app si
  // apre comunque (bug rotellina infinita, 19 ott 2026).
  const localeUnknown =
    user?.user_type === "dealer" &&
    user?.is_locale === undefined &&
    localePending;
  const isDealer = user?.user_type === "dealer" && user?.is_locale !== true;
  // FIX AGENT-MENU (Set 2026): flag dal server — mostra il menu Agente
  const isAgent = user?.is_agent === true;

  const isPremium =
    user?.subscription_tier &&
    user.subscription_tier !== "free" &&
    user.subscription_tier !== "rider_free";
  const hasActiveRiderSubscription =
    user?.rider_plan_code &&
    user.rider_plan_code !== "free" &&
    user.rider_plan_code !== "rider_free";
  const showPlusMenu = !isDealer && !isPremium && !hasActiveRiderSubscription;

  // FIX-CHAPTER-ADMIN: il rider è admin di un chapter se chapter_admin_of è valorizzato
  const chapterAdminOf = user?.chapter_admin_of ?? null;

  const totalNotifUnread = boardUnread + eventUnread + inAppUnread;

  const handleLogout = useCallback(async () => {
    try {
      await logoutApi();
    } catch {
      /* ignore network errors */
    } finally {
      logout();
    }
  }, [logout]);

  useWebSocket({
    onMessage: (msg: WsIncomingMessage) => {
      // FEAT DM-BADGE (Set 2026): incrementa il badge SOLO per messaggi
      // inviati da ALTRI utenti (il mittente multi-device non deve
      // vedersi il badge auto-incrementato).
      if (shouldIncrementDmUnread(msg, user?.id)) incrementDmUnread();
      // SUB-PLAN: il server notifica il cambio abbonamento in tempo reale.
      // Rileggiamo /api/auth/me e aggiorniamo lo store: le limitazioni
      // Free/Premium si aggiornano SUBITO, senza refresh manuali.
      if (msg.type === "subscription_updated") {
        import("@/api/core/apiFetch")
          .then(({ apiFetch }) => apiFetch("/api/auth/me").catch(() => null))
          .then((me: any) => {
            if (!me?.user) return;
            const token = useAuthStore.getState().accessToken;
            if (token) useAuthStore.getState().updateAccess(token, me.user);
          })
          .catch(() => {
            /* best-effort */
          });
      }
    },
  });

  useEffect(() => {
    let mounted = true;
    if (!user) return;
    const load = async () => {
      try {
        // FIX NOTIF-CACHE: la navbar monta a OGNI navigazione (3 chiamate
        // unread-count a ogni pagina). Con navigazioni rapide si superava il
        // rate limit (dm 30/min, board 60/min) -> 429 e warning. Cache 60s:
        // una sola raffica di chiamate al minuto per utente.
        const now = Date.now();
        if (now - unreadCache.ts < 60_000) {
          if (mounted)
            setAll(
              unreadCache.board,
              unreadCache.event,
              unreadCache.dm,
              unreadCache.inapp,
            );
          return;
        }
        const [b, dm, ev, ia, conn] = await Promise.all([
          getBoardUnreadCount(),
          getDmUnreadCount(),
          getEventNotificationsUnreadCount(),
          getInAppUnreadCount(),
          // FIX CERCHIA: conteggio richieste di connessione in sospeso.
          // Best-effort: se il biker non ha il piano Premium/Trial l'endpoint
          // risponde 403 — ignoriamo e teniamo 0 (il link resta visibile).
          fetchConnectionsUnread().catch(() => ({ pending_count: 0 })),
        ]);
        if (!mounted) return;
        unreadCache.ts = now;
        unreadCache.board = Number(b.unread || 0);
        unreadCache.event = Number(ev.unread || 0);
        unreadCache.dm = Number(dm.unread || 0);
        unreadCache.inapp = Number(ia.unread || 0);
        setAll(
          unreadCache.board,
          unreadCache.event,
          unreadCache.dm,
          unreadCache.inapp,
        );
        setConnectionPending(Number((conn as any)?.pending_count || 0));
      } catch {
        /* ignore */
      }
    };
    load();
    const t = window.setInterval(() => {
      // FIX NEON (Ago 2026): poll solo a tab visibile + ogni 10 min (prima 2 min
      // con 5 chiamate parallele) per permettere lo scale-to-zero di Neon.
      if (document.visibilityState === "visible") load();
    }, 600_000);
    return () => {
      mounted = false;
      window.clearInterval(t);
    };
  }, [user?.id, setAll]);

  /**
   * Chapter Admin Mode — persiste in localStorage.
   *
   * ATTENZIONE (bug reale 19 ott 2026): questi hook devono stare PRIMA di ogni
   * `return` anticipato (admin/dealer/chapter). Quando stavano dopo, un account
   * che passa da "dealer" a "locale" (flag is_locale che arriva dall'idratazione
   * di /api/auth/me) cambiava il ramo di ritorno e React lanciava
   * «Rendered more hooks than during the previous render» (#310).
   */
  const [chapterMode, setChapterMode] = useState(() => {
    try {
      return localStorage.getItem("rc-chapter-mode") === "true";
    } catch {
      return false;
    }
  });
  const toggleChapterMode = useCallback(() => {
    setChapterMode((prev) => {
      const next = !prev;
      try {
        localStorage.setItem("rc-chapter-mode", String(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  if (!user) return null;

  // Finché non sappiamo se l'account è un locale, non disegniamo niente:
  // prima comparivano per un attimo tutti i menu del concessionario.
  if (localeUnknown) return null;

  /* ── ADMIN ────────────────────────────────────────────── */
  if (isAdmin && !isDealer) {
    return (
      <>
        {/* Desktop sidebar */}
        <nav
          className={`desktop-sidebar hidden md:flex fixed left-0 top-0 h-screen flex-col bg-black border-r border-white/[0.20] p-6 z-50 overflow-y-auto transition-all duration-300 ${collapsed ? "w-16 p-3" : "w-64"}`}
        >
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              Ride<span className="text-brand-400">Connect</span>
            </h1>
            <p className="text-xs text-neutral-500 mt-1">Pannello Admin</p>
          </div>
          <div className="flex flex-col gap-2 flex-1">
            <SidebarLink
              to="/admin"
              icon={<Shield size={20} />}
              label="Admin Dashboard"
            />
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 p-2 rounded-lg text-neutral-500 hover:text-red-500 transition-colors mt-4"
            aria-label="Esci"
          >
            <LogOut size={20} />
            <span className="text-sm font-medium">Esci</span>
          </button>
        </nav>

        {/* Admin mobile nav — Priority+ pattern (4 tab + overflow) */}
        <AdminMobileNav onLogout={handleLogout} />
      </>
    );
  }

  /* ── DEALER ────────────────────────────────────────────── */
  if (isDealer) {
    return (
      <>
        {/* Desktop sidebar — organizzata in 4 sezioni logiche */}
        <nav
          className={`desktop-sidebar hidden md:flex fixed left-0 top-0 h-screen flex-col bg-black border-r border-white/[0.20] p-6 z-50 overflow-y-auto transition-all duration-300 ${collapsed ? "w-16 p-3" : "w-64"}`}
        >
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              Ride<span className="text-brand-400">Connect</span>
            </h1>
            <p className="text-xs text-neutral-500 mt-1">
              Pannello Concessionario
            </p>
          </div>

          <div className="flex flex-col gap-1 flex-1">
            {/* ── SEZIONE 1: BUSINESS ── */}
            <SectionHeader label="📊 Business" color="blue" />
            <SidebarLink
              to="/dealer/dashboard"
              icon={<BarChart3 size={20} />}
              label="Dashboard"
            />
            <SidebarLink
              to="/dealer/analytics"
              icon={<Activity size={20} />}
              label="Analytics"
            />

            {/* ── SEZIONE 2: VENDITA & CLIENTI ── */}
            <SectionHeader label="🏪 Vendita & Clienti" color="green" />
            <SidebarLink
              to="/dealer/leads"
              icon={<Columns size={20} />}
              label="Pipeline Lead"
            />
            <SidebarLink
              to="/dealer/appointments"
              icon={<Calendar size={20} />}
              label="Appuntamenti"
            />
            <SidebarLink
              to="/dealer/promotions"
              icon={<TrendingUp size={20} />}
              label="Promozioni"
            />
            <SidebarLink
              to="/dealer/followups"
              icon={<Bell size={20} />}
              label="Follow-up"
            />

            {/* ── SEZIONE 3: ESPOSIZIONE ── */}
            <SectionHeader label="🏍️ Esposizione" color="orange" />
            <SidebarLink
              to="/dealer/showcase"
              icon={<Store size={20} />}
              label="Vetrina"
            />
            <SidebarLink
              to="/dealer/rental"
              icon={<Bike size={20} />}
              label="Noleggio"
            />
            <SidebarLink
              to="/dealer/nearby"
              icon={<Eye size={20} />}
              label="Visibilità Locale"
            />

            {/* ── SEZIONE 4: COMMUNITY ── */}
            <SectionHeader label="🤝 Community" color="purple" />
            <SidebarLinkBadge
              to="/dealer/chapters"
              icon={<Users size={20} />}
              label="Chapters"
              badge={chapterRequests}
              badgeColor="bg-purple-500"
            />
            <SidebarLinkBadge
              to="/dealer/affiliations"
              icon={<UserCheck size={20} />}
              label="Affiliazioni"
              badge={affiliationRequests}
              badgeColor="bg-indigo-500"
            />
            <SidebarLink
              to="/dealer/events"
              icon={<Calendar size={20} />}
              label="I miei Eventi"
            />
          </div>

          {/* Utility links in fondo */}
          {/* FIX DEALER-NAV: /chats e /notifications sono route biker (RoleGate ->
              redirect a /dealer/dashboard) e le API board/dm negano i dealer.
              Il dealer ha: Affiliazioni (badge), Chapters (badge) e Profilo dealer. */}
          <div className="mt-3 pt-3 border-t border-white/[0.20] flex flex-col gap-1">
            {/* FIX DEALER-PROFILE (Set 2026): Profilo e Anagrafica Concessionaria
                erano due voci duplicate che puntavano alla stessa pagina —
                unite in un'unica voce. */}
            <SidebarLink
              to="/dealer/profile"
              icon={<Store size={20} />}
              label="Profilo & Anagrafica"
            />
            {/* FIX DEALER-MENU (Set 2026): 'Il mio Piano' spostato sotto
                Profilo & Anagrafica come richiesto. */}
            <SidebarLink
              to="/dealer/subscription"
              icon={<CreditCard size={20} />}
              label="Il mio Piano"
            />
            {/* FIX DEALER-NOTIFICHE (Ott 2026): il dealer riceve notifiche
                (affiliazioni, richieste garage) ma non aveva una voce per
                aprirle: la campanella lo rimbalzava sulla dashboard. */}
            <SidebarLink
              to="/notifications"
              icon={<Bell size={20} />}
              label="Notifiche"
            />
          </div>

          <button
            onClick={handleLogout}
            className="flex items-center gap-3 p-2 rounded-lg text-neutral-500 hover:text-red-500 transition-colors mt-4"
            aria-label="Esci"
          >
            <LogOut size={20} />
            <span className="text-sm font-medium">Esci</span>
          </button>
        </nav>

        {/* Mobile pill nav Dealer */}
        <DealerPillNav
          dmUnread={dmUnread}
          totalNotifUnread={totalNotifUnread}
          chapterRequests={chapterRequests}
          affiliationRequests={affiliationRequests}
          onLogout={handleLogout}
        />
      </>
    );
  }

  /* ── RIDER (default) ────────────────────────────────────────────── */

  // Se chapter admin mode è attivo, mostra la sidebar dedicata
  if (chapterMode && chapterAdminOf) {
    const cid = chapterAdminOf;
    return (
      <>
        {/* Desktop sidebar — Chapter Admin */}
        <nav
          className={`desktop-sidebar hidden md:flex fixed left-0 top-0 h-screen flex-col bg-black border-r border-white/[0.20] p-6 z-50 overflow-y-auto transition-all duration-300 ${collapsed ? "w-16 p-3" : "w-64"}`}
        >
          <div className="mb-4">
            <div className="flex items-center gap-2 mb-3">
              <button
                onClick={toggleChapterMode}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-orange-500/10 border border-orange-500/30 text-orange-400 hover:bg-orange-500/20 transition-all text-xs font-bold"
                aria-label="Torna a modalità Rider"
              >
                <Home size={12} />← Rider
              </button>
              <span className="text-[10px] text-orange-500/70 uppercase tracking-widest font-bold">
                Chapter Admin
              </span>
            </div>
            <h1 className="text-xl font-bold text-white flex items-center gap-2">
              <Shield size={18} className="text-orange-400" />
              Ride<span className="text-brand-400">Connect</span>
            </h1>
            <p className="text-xs text-neutral-500 mt-1">Gestione Chapter</p>
          </div>

          <div className="flex flex-col gap-1 flex-1">
            {/* ── CHAPTER HOME ── */}
            <SectionHeader label="🏠 Chapter Home" color="orange" />
            <SidebarLink
              to={`/chapters/${cid}/dashboard`}
              icon={<BarChart3 size={20} />}
              label="Dashboard"
            />
            <SidebarLink
              to={`/chapters/${cid}`}
              icon={<Building2 size={20} />}
              label="Profilo Pubblico"
            />

            {/* ── COMMUNITY ── */}
            <SectionHeader label="👥 Community" color="green" />
            <SidebarLink
              to={`/chapters/${cid}/roles`}
              icon={<Users size={20} />}
              label="Membri & Ruoli"
            />
            <SidebarLink
              to={`/chapters/${cid}/board`}
              icon={<MessageSquare size={20} />}
              label="Bacheca Forum"
            />

            {/* ── EVENTI & ATTIVITÀ ── */}
            <SectionHeader label="📅 Eventi & Attività" color="blue" />
            <SidebarLink
              to="/events/new"
              icon={<Calendar size={20} />}
              label="Crea Evento"
            />
            {/* RSVP e Sondaggi saranno aggiunti in P1.3 */}

            {/* ── COMPETIZIONI ── */}
            <SectionHeader label="⚔️ Competizioni" color="purple" />
            <SidebarLink
              to={`/chapters/${cid}/challenges`}
              icon={<Swords size={20} />}
              label="Chapter Wars"
            />
            <SidebarLink
              to={`/chapters/${cid}/stats`}
              icon={<Activity size={20} />}
              label="Statistiche KM"
            />
            <SidebarLink
              to="/chapter-wars"
              icon={<Trophy size={20} />}
              label="Badge & Rank"
            />

            {/* ── AMMINISTRAZIONE ── */}
            <SectionHeader label="💰 Amministrazione" color="orange" />
            <SidebarLink
              to={`/chapters/${cid}/quotas`}
              icon={<DollarSign size={20} />}
              label="Quote & Pagamenti"
            />
            <SidebarLink
              to={`/chapters/${cid}/documents`}
              icon={<FileText size={20} />}
              label="Documenti"
            />
            <SidebarLink
              to={`/chapters/${cid}/broadcast`}
              icon={<Megaphone size={20} />}
              label="Broadcast"
            />
          </div>

          {/* Utility links */}
          <div className="mt-3 pt-3 border-t border-white/[0.20] flex flex-col gap-1">
            <SidebarLink
              to="/chats"
              icon={<MessagesSquare size={20} />}
              label="Chat"
              badge={dmUnread}
            />
            <SidebarLink
              to="/notifications"
              icon={<Bell size={20} />}
              label="Notifiche"
              badge={totalNotifUnread}
            />
            <SidebarLink
              to="/profile"
              icon={<UserRound size={20} />}
              label="Profilo"
            />
          </div>

          <button
            onClick={handleLogout}
            className="flex items-center gap-3 p-2 rounded-lg text-neutral-500 hover:text-red-500 transition-colors mt-4"
            aria-label="Esci"
          >
            <LogOut size={20} />
            <span className="text-sm font-medium">Esci</span>
          </button>
        </nav>

        {/* Mobile: RiderPillNav standard (la chapter mode è solo desktop) */}
        <RiderPillNav
          dmUnread={dmUnread}
          totalNotifUnread={totalNotifUnread}
          connectionRequests={connectionPending}
          onLogout={handleLogout}
        />
      </>
    );
  }

  return (
    <>
      {/* Desktop sidebar — Rider */}
      <nav
        className={`desktop-sidebar hidden md:flex fixed left-0 top-0 h-screen flex-col bg-black border-r border-white/[0.20] p-6 z-50 overflow-y-auto transition-all duration-300 ${collapsed ? "w-16 p-3" : "w-64"}`}
      >
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            Ride<span className="text-brand-400">Connect</span>
          </h1>
          <p className="text-xs text-neutral-500 mt-1">
            Per chi vive la strada
          </p>
        </div>

        {/* CHAPTER-ADMIN TOGGLE: se l'utente gestisce un chapter, mostra il pulsante */}
        {chapterAdminOf && (
          <button
            onClick={toggleChapterMode}
            className="flex items-center gap-2 mb-4 px-3 py-2 rounded-xl bg-orange-500/10 border border-orange-500/20 hover:bg-orange-500/20 transition-all text-orange-400"
            aria-label="Attiva modalità Chapter Admin"
          >
            <Shield size={18} />
            <span className="text-sm font-bold">🛡️ Chapter Admin</span>
            <ChevronRight size={14} className="ml-auto" />
          </button>
        )}

        <div className="flex flex-col gap-2 flex-1">
          {/* LOCALE/TAPPA (Ott 2026): il locale non ha il mondo biker — la sua
              unica area è il pannello della tappa. Voce in evidenza per poterci
              tornare da qualsiasi pagina, senza restare senza uscita. */}
          {user?.is_locale && (
            <Link
              to="/locale"
              className="mb-2 flex items-center gap-2 rounded-xl border border-teal-400/40 bg-teal-400/10 px-3 py-2 text-sm font-bold text-teal-300 hover:bg-teal-400/20 transition"
            >
              <Store size={18} />
              Pannello locale
              <ChevronRight size={14} className="ml-auto" />
            </Link>
          )}
          {!collapsed && (
            <SectionHeader label="🏍️ Il mio mondo" color="neutral" />
          )}
          <SidebarLink
            to="/dashboard"
            icon={<Home size={20} />}
            label="Dashboard"
          />
          <SidebarLink
            to="/garage"
            icon={<Wrench size={20} />}
            label="Garage"
          />
          <SidebarLink
            to="/garage/marketplace"
            icon={<Tag size={20} />}
            label="Marketplace"
          />
          {/* OTT 2026: inviti a entrare nella squadra di un locale. */}
          <SidebarLink
            to="/inviti-squadra"
            icon={<Store size={20} />}
            label="Inviti squadra"
          />
          {/* RIFINITURA UX (audit vs Rombo): gruppi visivi per la scansione */}
          {!collapsed && (
            <SectionHeader label="🚀 Uscite & Eventi" color="blue" />
          )}
          <SidebarLink to="/routes" icon={<Map size={20} />} label="Percorsi" />
          <SidebarLink
            to="/events"
            icon={<Calendar size={20} />}
            label="Eventi"
          />
          {/* FIX NOTIFICHE-APPT: menu biker per confermare appuntamenti dealer */}
          <SidebarLink
            to="/appointments"
            icon={<CalendarCheck size={20} />}
            label="Appuntamenti"
          />
          <SidebarLink
            to="/nearby"
            icon={<LocateFixed size={20} />}
            label="Vicini"
          />
          <SidebarLink
            to="/radar"
            icon={<Activity size={20} />}
            label="Statistiche"
          />
          {!collapsed && <SectionHeader label="👥 Community" color="green" />}
          <SidebarLink
            to="/cerchia"
            icon={<Users size={20} />}
            label="Cerchia"
            badge={connectionPending}
          />
          <SidebarLink
            to="/dealers"
            icon={<Store size={20} />}
            label="Concessionari"
          />
          {/* FIX MENU-ORDER (Set 2026): Bacheca e Chat vicine, in fondo */}
          <SidebarLink
            to="/board"
            icon={<MessageSquare size={20} />}
            label="Bacheca"
            badge={boardUnread}
          />
          <SidebarLink
            to="/chats"
            icon={<MessagesSquare size={20} />}
            label="Chat"
            badge={dmUnread}
          />
          {!collapsed && <SectionHeader label="👤 Account" color="orange" />}
          <SidebarLink
            to="/notifications"
            icon={<Bell size={20} />}
            label="Notifiche"
            badge={totalNotifUnread}
          />
          {/* FIX AGENT-MENU (Set 2026): area dedicata agente commerciale */}
          {isAgent && (
            <SidebarLink
              to="/agent/dashboard"
              icon={<Shield size={20} />}
              label="Area Agente"
            />
          )}
          <SidebarLink
            to="/profile"
            icon={<UserRound size={20} />}
            label="Profilo"
          />
          {showPlusMenu && (
            <SidebarLink
              to="/pricing"
              icon={<Crown size={20} />}
              label="Abbonamento"
            />
          )}
          {/* ASSISTENZA (Ott 2026): le FAQ prima del modulo di contatto —
              la maggior parte dei dubbi si risolve senza scrivere. */}
          <SidebarLink
            to="/aiuto"
            icon={<HelpCircle size={20} />}
            label="Aiuto"
          />

          {/* CHAPTER-ADMIN: link rapido nella sidebar Rider */}
          {chapterAdminOf && !chapterMode && (
            <>
              <div className="my-2 border-t border-white/[0.20]" />
              <Link
                to={`/chapters/${chapterAdminOf}/dashboard`}
                className={`flex flex-row items-center gap-3 p-2 rounded-lg transition-colors ${
                  isActivePath(`/chapters/${chapterAdminOf}/dashboard`)
                    ? "text-brand-500"
                    : "text-orange-400 hover:text-orange-300"
                }`}
              >
                <div className="relative">
                  <Shield size={20} />
                  <Crown
                    size={10}
                    className="absolute -top-1.5 -right-1.5 text-orange-400 fill-orange-400"
                  />
                </div>
                <span className="text-sm font-semibold">Il mio Chapter</span>
              </Link>
            </>
          )}
        </div>

        <button
          onClick={handleLogout}
          className="flex items-center gap-3 p-2 rounded-lg text-neutral-500 hover:text-red-500 transition-colors mt-4"
          aria-label="Esci"
        >
          <LogOut size={20} />
          <span className="text-sm font-medium">Esci</span>
        </button>
      </nav>

      {/* Mobile pill nav */}
      <RiderPillNav
        dmUnread={dmUnread}
        totalNotifUnread={totalNotifUnread}
        connectionRequests={connectionPending}
        onLogout={handleLogout}
      />
    </>
  );
};

export default Navbar;
